import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { FilesViewContext } from '../file-view-context';
import { FilesDataSource } from '../files-datasource';
import { FilesSearchAndSortComponent } from '../files-search-and-sort/files-search-and-sort.component';
import { FileModelHelper, FileModelView } from '../file-model-helper';
import { FileDescriptionInputComponent } from '../file-description-input/file-description-input.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { SafeDatePipe } from 'src/app/shared/pipes/fix-date/fix-date.pipe';

@Component({
  selector: 'app-file-details',
  imports: [
    AsyncPipe,
    SafeDatePipe,
    FileDescriptionInputComponent,
    FilesSearchAndSortComponent,
    L10nTranslatePipe,
    MatIconModule,
    MatListModule,
    MatSidenavModule
  ],
  templateUrl: './file-details.component.html',
  styleUrl: './file-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileDetailsComponent {
  private readonly auth = inject(AuthService);
  protected get isSuperAdmin() { return this.auth.isSuperAdmin(); }

  public readonly locale = inject(L10N_LOCALE);
  public readonly viewContext = input.required<FilesViewContext>();
  public readonly datasource = input.required<FilesDataSource>();
  public readonly fileDescription = input.required<FormControl>();
  public readonly fileResize = input<FormControl>(null);

  public readonly fileToImport = input<FileModelView | null>();
  public readonly selectedFile = input<FileModelView | null>();
  protected get fileToDisplay() { return this.selectedFile ?? this.fileToImport ?? null; }

  public readonly fileSearch = input.required<FormControl>();

  protected onFileSelected(event: MatSelectionListChange) {
    const selected = event.options[0]?.value ?? null;
    if (selected) {
      this.viewContext().fileSelected.emit(selected);
    }
  }

  protected onFileSelectedEvent() {
    this.viewContext().useFile.emit();
  }

  protected onFileDeletedEvent() {
    this.viewContext().deleteSelected.emit();
  }

  protected onFileSavedEvent() {
    this.viewContext().saveFile.emit();
  }

  protected onUploadCancelled() {
    this.viewContext().uploadCancelled.emit();
  }
  protected onFileUploadConfirmed() {
    this.viewContext().uploadConfirmed.emit();
  }

  protected isSelected(file: FileModelView) {
    return this.datasource().selection.isSelected(file);
  }

  protected getContentType(file: FileModelView) {
    return FileModelHelper.getContentType(file);
  }

  protected isImageFile(file: FileModelView) {
    return FileModelHelper.isImageFile(file);
  }

  protected getIcon(file: FileModelView) {
    return FileModelHelper.getIcon(file);
  }

  protected getDate(file: FileModelView) {
    return FileModelHelper.getDate(file);
  }
}
